/**
 * EditNodeModal - Modal for editing existing nodes
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Pencil, Loader2, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { useMapStore } from '../../stores/mapStore';

const EditNodeModal = () => {
  const {
    editModalNodeId,
    closeEditModal,
    updateNode,
    nodes,
  } = useMapStore();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [showContext, setShowContext] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Find the current node
  const currentNode = nodes.find(n => n.id === editModalNodeId);

  // Initialize form with current node data
  useEffect(() => {
    if (currentNode?.data) {
      setTitle(currentNode.data.title || '');
      setContent(currentNode.data.content || '');
      setAdditionalContext(currentNode.data.additional_context || '');
      setShowContext(!!currentNode.data.additional_context);
    }
  }, [currentNode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editModalNodeId) return;

    setIsSubmitting(true);
    try {
      await updateNode(editModalNodeId, {
        title: title.trim() || undefined,
        content: content.trim(),
        additional_context: additionalContext.trim() || null,
      });
      closeEditModal();
    } catch (error) {
      console.error('Error updating node:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!editModalNodeId || !currentNode) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && closeEditModal()}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-lg bg-gray-900 border border-gray-800 rounded-xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <Pencil size={20} className="text-indigo-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">Notu Düzenle</h2>
              <p className="text-xs text-gray-500">{currentNode?.data?.type || 'text'} node</p>
            </div>
          </div>
          <button
            onClick={closeEditModal}
            className="p-1.5 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1.5">
              Baslik
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Baslik..."
              className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1.5">
              Icerik
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Not icerigi..."
              rows={6}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors resize-none"
            />
          </div>

          {/* Additional Context (Collapsible) */}
          <div className="border-t border-gray-800 pt-4">
            <button
              type="button"
              onClick={() => setShowContext(!showContext)}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300 transition-colors"
            >
              <FileText size={14} />
              <span>Ek Bilgi / Baglam</span>
              {showContext ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {additionalContext && !showContext && (
                <span className="ml-2 px-1.5 py-0.5 bg-indigo-500/20 text-indigo-400 text-xs rounded">
                  Mevcut
                </span>
              )}
            </button>

            {showContext && (
              <div className="mt-3">
                <textarea
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  placeholder="Email ozeti, toplanti notlari, referans bilgiler..."
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors resize-none text-sm"
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  Bu alan AI tarafindan baglam olarak kullanilacak ama ana icerikten ayri tutulacak
                </p>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={closeEditModal}
              className="flex-1 px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-750 transition-colors"
            >
              Iptal
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !content.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Kaydediliyor...</span>
                </>
              ) : (
                <span>Kaydet</span>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default EditNodeModal;
